import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackServicePlaceClick, trackServicePlaceShow } from './tracking';
import { IAppState } from '../../../common/state';
import { sendTeaserEvent, ITeaserEventProps } from '../../../utils/TeaserEvent';
import { ServicePlacesMortgageBanner } from '../../components/ServicePlacesMortgageBanner';
import { selectSource } from 'shared/selectors';
import { mortgageDataFlatRatesSelector } from 'shared/selectors/mortgageData';
import { selectJsonQuery, selectQueryString } from 'shared/selectors/results';
import { getUser } from 'shared/selectors/user';
import { IIntegrateAdOptions } from 'shared/serp/components/offers/types';
import { TUser } from 'shared/types/user';

export const getServicePlaceWidgetName = (position: number) => {
  switch (position) {
    case 2:
      return 'servisnoe_mesto_oldstyle_3_show';
    case 32:
      return 'servisnoe_mesto_oldstyle_33_show';
    case 62:
      return 'servisnoe_mesto_oldstyle_63_show';
    default:
      return '';
  }
};

export interface IServicePlacesMortgageBannerContainerProps {
  options: IIntegrateAdOptions;
  position: number;
}

export const getTeaserProps = ({
  props,
  isViewed,
}: {
  props: IServicePlacesMortgageBannerContainerProps & {
    currentResultPageNumber?: number;
    deviceType?: string;
    queryString?: string;
    soprApi?: string;
    user: TUser;
  };
  isViewed: boolean;
}): ITeaserEventProps => ({
  soprApi: props.soprApi || '',
  user_id: (props.user && props.user.isAuthenticated && props.user.userId) || null,
  pagenumber: 1,
  position: props.position || 1,
  pageType: 'Listing',
  device_type: props.deviceType || '',
  block_type: 'mortgage_widget',
  sc: props.queryString || '',
  name: getServicePlaceWidgetName(props.position),
  isTeaserShow: isViewed ? 1 : 0,
});

export const ServicePlacesMortgageBannerContainer: React.FC<IServicePlacesMortgageBannerContainerProps> = props => {
  const soprApi = useSelector((state: IAppState) => state.soprApi);
  const deviceType = useSelector((state: IAppState) => state.deviceType);
  const user = useSelector(getUser);
  const queryString = useSelector(selectQueryString);
  const rates = useSelector(mortgageDataFlatRatesSelector);
  const jsonQuery = useSelector(selectJsonQuery);
  const source = useSelector(selectSource);

  const mergedProps = React.useMemo(
    () => ({
      ...props,
      soprApi,
      deviceType,
      user,
      observerKey: queryString,
      queryString,
    }),
    [props, soprApi, deviceType, user, queryString],
  );

  React.useEffect(() => {
    if (!rates) {
      return;
    }

    sendTeaserEvent([getTeaserProps({ props: mergedProps, isViewed: false })]);
  }, [mergedProps, rates]);

  const handleView = React.useCallback(() => {
    sendTeaserEvent([
      getTeaserProps({
        props: mergedProps,
        isViewed: true,
      }),
    ]);
    trackServicePlaceShow(jsonQuery, props.position, source);
  }, [jsonQuery, mergedProps, props.position, source]);

  const handleClick = React.useCallback(() => {
    trackServicePlaceClick(jsonQuery, props.position, source);
  }, [jsonQuery, props.position, source]);
  if (!rates) {
    return null;
  }

  return (
    <ActionAfterViewObserver callback={handleView} triggerOnce>
      <ServicePlacesMortgageBanner position={props.position} onClick={handleClick} rates={rates} />
    </ActionAfterViewObserver>
  );
};
